import axios from 'axios';
import type {
  ApercuMail, CreerInstanceInput, EnvoiInput, Instance, InstanceCreee, InstancesParChemin, ModeleProposable, ResultatEnvoi,
} from './types';

// Appels des formulaires JOINTS aux dossiers (panneau « Formulaires » du dossier, « Joindre un formulaire »,
// « Nouveau dossier ») pour une base d'URL donnée :
//   - admin : `${BASE_URL}/formulaires`, sans auth (apiFormulairesAdmin de services/formulairesServices.ts) ;
//   - admin Legrand : `${BASE_URL}/api/legrand/formulaires`, token Legrand, dossiers Legrand seulement, écriture réservée
//     au superadmin, jeton masqué en lecture seule (backend : routes/legrandFormulaires.js).
// Les composants la reçoivent par ApiFormulairesContext. Les modèles du menu Formulaires restent dans formulairesServices.
// Pas de try/catch : l'erreur axios remonte au composant (messageErreurs).

export interface ApiFormulaires {
  // Modèles actifs proposables (le routeur Legrand ne renvoie que ces champs)
  listerModelesActifs: () => Promise<ModeleProposable[]>;
  // Panneau du dossier : dossier (source, e-mail…) + instances id DESC ; 404 si chemin invalide/inconnu/hors périmètre
  instancesParChemin: (chemin: string) => Promise<InstancesParChemin>;
  // 201 { id, jeton, url, statut: 'cree', email_destinataire, nom_modele } ; 403 si source ≠ admin_manuel ; aucun mail
  creerInstance: (d: CreerInstanceInput) => Promise<InstanceCreee>;
  // Composeur pré-rempli ; relance omis = décidé par le serveur (nb_envois > 0) ; 409 soumis/annulé ; 502 template désactivé
  apercuMailInstance: (id: number, relance?: boolean) => Promise<ApercuMail>;
  // Envoi / relance : corps 200 renvoyé tel quel ; 400 (e-mail, corps sans le lien) ; 409 ; 502 { message, mail: { envoye: false, erreur } }
  envoyerInstance: (id: number, d: EnvoiInput) => Promise<ResultatEnvoi>;
  // soumis → en_cours (même lien) ; 409 sinon
  rouvrirInstance: (id: number) => Promise<Instance>;
  // → annule (terminal, le lien du client répond 410) ; 409 si déjà annulé
  annulerInstance: (id: number) => Promise<Instance>;
  // 204 seulement si statut 'cree' et jamais rempli ; 409 { message: 'Annulez plutôt ce formulaire' } sinon
  supprimerInstance: (id: number) => Promise<void>;
}

// enTetes est appelée à chaque requête : le token est relu, jamais figé au chargement du module
export const creerApiFormulaires = (base: string, enTetes: () => Record<string, string> = () => ({})): ApiFormulaires => {
  const cfg = () => ({ headers: enTetes() });
  return {
    listerModelesActifs: async () => (await axios.get<ModeleProposable[]>(`${base}/modeles?actifs=1`, cfg())).data,
    instancesParChemin: async (chemin) =>
      (await axios.get<InstancesParChemin>(`${base}/dossiers/par-chemin/${encodeURIComponent(chemin)}`, cfg())).data,
    creerInstance: async (d) => (await axios.post<InstanceCreee>(`${base}/dossiers`, d, cfg())).data,
    apercuMailInstance: async (id, relance) =>
      (await axios.get<ApercuMail>(`${base}/dossiers/${id}/apercu-mail${relance === undefined ? '' : `?relance=${relance ? 1 : 0}`}`, cfg())).data,
    envoyerInstance: async (id, d) => (await axios.post<ResultatEnvoi>(`${base}/dossiers/${id}/envoyer`, d, cfg())).data,
    rouvrirInstance: async (id) => (await axios.put<Instance>(`${base}/dossiers/${id}/rouvrir`, {}, cfg())).data,
    annulerInstance: async (id) => (await axios.put<Instance>(`${base}/dossiers/${id}/annuler`, {}, cfg())).data,
    supprimerInstance: async (id) => { await axios.delete(`${base}/dossiers/${id}`, cfg()); },
  };
};
